import { useState } from "react";
import { motion } from "framer-motion";
import {
  MessageCircle,
  Phone,
  MapPin,
  Clock,
  Mail,
  Globe,
  Menu,
  X,
  Heart,
  PawPrint,
  Instagram,
} from "lucide-react";
import {
  CLINIC,
  SERVICES,
  TELEMEDICINA,
  IMAGES,
  waLink,
  mapsEmbed,
} from "@/content";
import { ServiceIcon } from "@/icons";

const C = {
  bg: "#FAF6F0",
  card: "#FFFFFF",
  primary: "#D97757",
  primaryHover: "#C05F41",
  secondary: "#3D5A45",
  accent: "#F59E0B",
  text: "#2D2621",
  muted: "#6E6359",
  border: "#E8DEC9",
};

const NAV = [
  { href: "#servicios", label: "Servicios" },
  { href: "#telemedicina", label: "Consulta Online" },
  { href: "#contacto", label: "Contacto" },
];

const fade = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08 },
  }),
};

export default function ProposalWarm() {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="font-body-warm"
      style={{ background: C.bg, color: C.text }}
      data-testid="proposal-warm"
    >
      {/* Header */}
      <header
        className="sticky top-0 z-40 backdrop-blur-md"
        style={{ background: "rgba(250,246,240,0.85)", borderBottom: `1px solid ${C.border}` }}
      >
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <a href="#inicio" className="flex items-center gap-2" data-testid="warm-logo">
            <span
              className="grid place-items-center h-9 w-9 rounded-full"
              style={{ background: C.primary }}
            >
              <PawPrint className="h-5 w-5 text-white" />
            </span>
            <span className="font-serif-warm text-lg font-semibold leading-none">
              Los Orientales
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-7">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                className="text-sm transition-colors hover:opacity-70"
                style={{ color: C.muted }}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={CLINIC.instagramHref}
              target="_blank"
              rel="noreferrer"
              data-testid="warm-header-instagram"
              aria-label="Instagram"
              className="hidden sm:grid place-items-center h-10 w-10 rounded-full border transition-colors hover:bg-white"
              style={{ borderColor: C.border, color: C.primary }}
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="warm-header-whatsapp"
              className="hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white transition-transform hover:scale-105"
              style={{ background: C.primary }}
            >
              <MessageCircle className="h-4 w-4" />
              Agendar
            </a>
            <button
              className="md:hidden p-2"
              onClick={() => setOpen((v) => !v)}
              data-testid="warm-menu-toggle"
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden px-5 pb-4 flex flex-col gap-3" style={{ borderTop: `1px solid ${C.border}` }}>
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setOpen(false)} className="pt-3 text-sm" style={{ color: C.muted }}>
                {n.label}
              </a>
            ))}
          </div>
        )}
      </header>

      {/* Hero */}
      <section id="inicio" className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div variants={fade} initial="hidden" animate="show">
            <span className="font-script text-2xl" style={{ color: C.primary }}>
              Cariño que se nota
            </span>
            <h1 className="mt-2 font-serif-warm text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
              Medicina integral de perros y gatos en Ñuñoa
            </h1>
            <p className="mt-5 text-base sm:text-lg leading-relaxed" style={{ color: C.muted }}>
              Un equipo cercano y profesional que cuida a tu mascota como parte
              de la familia. Atención presencial en Ñuñoa y consulta online para
              todo Chile.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={waLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="warm-hero-whatsapp"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-white font-semibold shadow-lg transition-transform hover:scale-105"
                style={{ background: C.primary }}
              >
                <MessageCircle className="h-5 w-5" />
                Agendar hora por WhatsApp
              </a>
              <a
                href="#servicios"
                className="inline-flex items-center gap-2 rounded-full border px-6 py-3.5 font-semibold"
                style={{ borderColor: C.secondary, color: C.secondary }}
              >
                Ver servicios
              </a>
            </div>
            <div className="mt-8 flex items-center gap-5 text-sm" style={{ color: C.muted }}>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" style={{ color: C.primary }} />{CLINIC.hoursShort}</span>
              <span className="flex items-center gap-1.5"><Heart className="h-4 w-4" style={{ color: C.primary }} />Perros y gatos</span>
            </div>
            <div
              className="mt-6 inline-flex rounded-2xl bg-white shadow-sm"
              style={{ border: `1px solid ${C.border}` }}
              data-testid="warm-hero-metro"
            >
              <img
                src={IMAGES.metro}
                alt="Metro Los Orientales — Salida E. Castillo Velasco"
                className="h-12 sm:h-14 w-auto object-contain"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div
              className="absolute -inset-6 -z-10 rounded-[3rem] rotate-3"
              style={{ background: C.accent, opacity: 0.18 }}
            />
            <img
              src={IMAGES.womanDog}
              alt="Tutora abrazando a su perro"
              className="w-full h-[26rem] object-cover rounded-[2.5rem] shadow-xl"
            />
            <div
              className="absolute -bottom-5 -left-5 rounded-2xl bg-white px-5 py-4 shadow-lg"
              style={{ border: `1px solid ${C.border}` }}
            >
              <p className="font-serif-warm text-2xl font-semibold" style={{ color: C.secondary }}>+20 años</p>
              <p className="text-xs" style={{ color: C.muted }}>sanando mascotas</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Servicios */}
      <section id="servicios" className="py-16 sm:py-24" style={{ background: "#fff" }}>
        <div className="max-w-6xl mx-auto px-5">
          <SectionHead color={C} eyebrow="Nuestros servicios" title="Todo lo que tu mascota necesita, en un solo lugar" />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.id}
                data-testid={`warm-service-${s.id}`}
                custom={i}
                variants={fade}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
                className="group rounded-3xl p-6 transition-all hover:-translate-y-1.5"
                style={{ background: C.bg, border: `1px solid ${C.border}` }}
              >
                <div
                  className="grid place-items-center h-12 w-12 rounded-2xl mb-4 transition-colors"
                  style={{ background: s.highlight ? C.secondary : `${C.primary}1a`, color: s.highlight ? "#fff" : C.primary }}
                >
                  <ServiceIcon name={s.icon} className="h-6 w-6" />
                </div>
                <h3 className="font-serif-warm text-xl font-semibold flex items-center gap-2">
                  {s.title}
                  {s.highlight && (
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-body-warm text-white" style={{ background: C.accent }}>
                      {s.highlight}
                    </span>
                  )}
                </h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: C.muted }}>
                  {s.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Telemedicina */}
      <section id="telemedicina" className="py-16 sm:py-24" style={{ background: C.secondary, color: "#F3EFE7" }}>
        <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="font-script text-2xl" style={{ color: C.accent }}>Estés donde estés</span>
            <h2 className="mt-1 font-serif-warm text-3xl sm:text-4xl font-semibold">{TELEMEDICINA.title}</h2>
            <p className="mt-4 text-base" style={{ color: "#D8D2C4" }}>{TELEMEDICINA.subtitle}</p>
            <ul className="mt-6 space-y-3">
              {TELEMEDICINA.features.map((f) => (
                <li key={f} className="flex gap-3 text-sm">
                  <span className="mt-1 h-2 w-2 rounded-full flex-shrink-0" style={{ background: C.accent }} />
                  {f}
                </li>
              ))}
            </ul>
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="warm-telemed-whatsapp"
              className="mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-semibold text-white transition-transform hover:scale-105"
              style={{ background: C.primary }}
            >
              <MessageCircle className="h-5 w-5" />
              Agendar consulta online
            </a>
          </div>
          <img src={IMAGES.telemed} alt="Consulta online veterinaria" className="w-full h-80 object-cover rounded-[2.5rem] shadow-2xl" />
        </div>
      </section>

      {/* Contacto */}
      <section id="contacto" className="py-16 sm:py-24" style={{ background: "#fff" }}>
        <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-2 gap-10">
          <div>
            <SectionHead color={C} eyebrow="Visítanos" title="Estamos en el corazón de Ñuñoa" align="left" />
            <div className="mt-8 space-y-5">
              <ContactRow color={C} icon={MapPin} label="Dirección" value={CLINIC.address} />
              <ContactRow color={C} icon={Clock} label="Horario" value={CLINIC.hours} />
              <ContactRow color={C} icon={Phone} label="Teléfono" value={CLINIC.phone} href={`tel:${CLINIC.phoneHref}`} />
              <ContactRow color={C} icon={MessageCircle} label="WhatsApp" value={CLINIC.whatsapp} href={waLink()} />
              <ContactRow color={C} icon={Mail} label="Email" value={CLINIC.email} href={`mailto:${CLINIC.email}`} />
              <ContactRow color={C} icon={Instagram} label="Instagram" value={CLINIC.instagram} href={CLINIC.instagramHref} />
              <ContactRow color={C} icon={Globe} label="Sitio web" value={CLINIC.website} href={CLINIC.websiteHref} />
            </div>
            <div
              className="mt-8 rounded-3xl overflow-hidden bg-white shadow-sm"
              style={{ border: `1px solid ${C.border}` }}
              data-testid="warm-contact-metro"
            >
              <img
                src={IMAGES.metro}
                alt="Metro Los Orientales — Salida E. Castillo Velasco"
                className="w-full object-contain"
              />
            </div>
          </div>
          <div className="rounded-[2.5rem] overflow-hidden shadow-lg min-h-[22rem]" style={{ border: `1px solid ${C.border}` }}>
            <iframe title="Mapa Los Orientales" src={mapsEmbed()} className="w-full h-full min-h-[22rem]" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10" style={{ background: C.secondary, color: "#E8E2D6" }}>
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <PawPrint className="h-5 w-5" style={{ color: C.accent }} />
            <span className="font-serif-warm text-lg">{CLINIC.name}</span>
          </div>
          <p style={{ color: "#B9B3A4" }}>{CLINIC.commune} · {CLINIC.hoursShort}</p>
          <div className="flex items-center gap-4">
            <a href={CLINIC.instagramHref} target="_blank" rel="noreferrer" data-testid="warm-footer-instagram" aria-label="Instagram" className="inline-flex items-center gap-2 transition-opacity hover:opacity-80" style={{ color: C.accent }}>
              <Instagram className="h-5 w-5" />
              {CLINIC.instagram}
            </a>
            <a href={CLINIC.websiteHref} target="_blank" rel="noreferrer" className="underline underline-offset-4" style={{ color: C.accent }}>
              {CLINIC.website}
            </a>
          </div>
        </div>
      </footer>

      <FloatWhatsApp bg={C.primary} />
    </motion.div>
  );
}

const SectionHead = ({ color, eyebrow, title, align = "center" }) => (
  <div className={align === "center" ? "text-center max-w-2xl mx-auto" : ""}>
    <p className="font-script text-2xl" style={{ color: color.primary }}>{eyebrow}</p>
    <h2 className="mt-1 font-serif-warm text-3xl sm:text-4xl font-semibold" style={{ color: color.text }}>{title}</h2>
  </div>
);

const ContactRow = ({ color, icon: Icon, label, value, href }) => {
  const inner = (
    <div className="flex gap-4">
      <span className="grid place-items-center h-11 w-11 rounded-2xl flex-shrink-0" style={{ background: `${color.primary}1a`, color: color.primary }}>
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className="text-xs uppercase tracking-wide" style={{ color: color.muted }}>{label}</p>
        <p className="font-medium" style={{ color: color.text }}>{value}</p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" className="block transition-opacity hover:opacity-70">{inner}</a>
  ) : inner;
};

const FloatWhatsApp = ({ bg }) => (
  <a
    href={waLink()}
    target="_blank"
    rel="noreferrer"
    data-testid="warm-float-whatsapp"
    className="fixed bottom-5 right-5 z-50 grid place-items-center h-14 w-14 rounded-full text-white shadow-2xl transition-transform hover:scale-110"
    style={{ background: "#25D366" }}
    aria-label="WhatsApp"
  >
    <MessageCircle className="h-7 w-7" />
  </a>
);
